import type { User } from '@supabase/supabase-js';

import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { ServiceResponse } from '../base/ServiceTypes';

import type { Database } from '@/integrations/supabase/types';

type UserProfileRow = Database['public']['Tables']['user_profiles']['Row'];
type UserProfileUpdate = Database['public']['Tables']['user_profiles']['Update'];

export interface UserMetadataUpdate {
  company_name?: string;
  [key: string]: unknown;
}

export class UserProfileService extends BaseService {
  private static instance: UserProfileService;

  private constructor() {
    super({
      defaultTTL: 5 * 60 * 1000, // 5 minutes
      enableCache: true,
      enableRetry: true,
    });
  }

  public static getInstance(): UserProfileService {
    if (!UserProfileService.instance) {
      UserProfileService.instance = new UserProfileService();
    }
    return UserProfileService.instance;
  }

  /**
   * Get the current user's profile
   */
  async getUserProfile(): Promise<ServiceResponse<UserProfileRow | null>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const cacheKey = `user-profile-${userId}`;

      return this.executeWithCache(cacheKey, async () => {
        const { data, error } = await this.client
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (error) {
          throw error;
        }

        return data;
      });
    });
  }

  /**
   * Update user metadata (stored in auth.users table)
   */
  async updateUserMetadata(updates: UserMetadataUpdate): Promise<ServiceResponse<User>> {
    return this.execute(async () => {
      const {
        data: { user },
      } = await this.client.auth.getUser();
      if (!user) {
        throw ServiceError.unauthorized();
      }

      // Merge with existing metadata
      const currentMetadata = user.user_metadata || {};
      const mergedMetadata = { ...currentMetadata, ...updates };

      const { data, error } = await this.client.auth.updateUser({
        data: mergedMetadata,
      });

      if (error) throw error;
      if (!data.user) throw new Error('Failed to update user metadata');

      // Clear user-related caches
      this.clearCache(`user-profile-${user.id}`);

      return data.user;
    }, 'Failed to update user metadata');
  }

  /**
   * Update user profile in the database
   */
  async updateUserProfile(updates: UserProfileUpdate): Promise<ServiceResponse<UserProfileRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.client
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .maybeSingle();

      if (error) {
        throw error;
      }

      if (!data) {
        throw ServiceError.notFound('User profile');
      }

      // Clear cache
      this.clearCache(`user-profile-${userId}`);

      return data;
    }, 'Failed to update user profile');
  }

  /**
   * Create or update user profile
   */
  async upsertUserProfile(
    profile: Partial<UserProfileRow>,
  ): Promise<ServiceResponse<UserProfileRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const { data, error } = await this.client
        .from('user_profiles')
        .upsert({
          ...profile,
          id: userId,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw ServiceError.notFound('User profile');

      // Clear cache
      this.clearCache(`user-profile-${userId}`);

      return data;
    }, 'Failed to upsert user profile');
  }

  /**
   * Clear all user-related caches
   */
  async clearUserCaches(): Promise<void> {
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (user) {
      this.clearCache(`user-profile-${user.id}`);
    }
  }
}

// Export singleton instance
export const userProfileService = UserProfileService.getInstance();
